import cx from 'classnames';
import { defineMessages, useIntl } from 'react-intl';
import config from '@plone/volto/registry';
import { useSelector, type TypedUseSelectorHook } from 'react-redux';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import MaybeWrap from '@plone/volto/components/manage/MaybeWrap/MaybeWrap';

import styles from './styles.module.css';
import type { QubicaContactCardsStyleProps, QubicaViewProps } from './utils';

interface RootState {
  userSession: { token: string | null };
}

const RepresentativesCards = (
  props: QubicaViewProps & QubicaContactCardsStyleProps,
) => {
  const intl = useIntl();
  const Image = config.getComponent('Image').component;

  const useTypedSelector: TypedUseSelectorHook<RootState> =
    useSelector<RootState>;

  const { content, bg } = props;

  const jobtitle =
    typeof content.jobtitle === 'object'
      ? content.jobtitle?.title
      : content.jobtitle;
  const address = `${content.address ?? ''}${(content.address && (content.zipcode || content.city) && ', ') ?? ''}${content.zipcode ?? ''}${(content.zipcode && content.city && ' ') ?? ''}${content.city ?? ''}`;

  const userIsLogged = useTypedSelector((state) => !!state.userSession.token);

  return (
    <div
      className={cx(styles.representativeCard, {
        [styles.whiteBg]: bg === 'white',
        [styles.grayBg]: bg === 'gray',
      })}
    >
      {content.image_scales
        ? Object.keys(content.image_scales).length > 0 && (
            <div className={styles.representativeCardImageContainer}>
              <Image
                item={content}
                alt={content.title}
                className={styles.cardImage}
              />
            </div>
          )
        : content.image && (
            <div className={styles.representativeCardImageContainer}>
              <Image
                item={content}
                alt={content.title}
                className={styles.cardImage}
              />
            </div>
          )}
      <div className={styles.representativeCardTextContainer}>
        <h4
          className={cx(
            styles.representativeCardTitle,
            styles.representativeCardLink,
          )}
        >
          <MaybeWrap
            // @ts-expect-error wrong types for MaybeWrap, fixed in a later volto version
            as={UniversalLink}
            condition={userIsLogged}
            href={content['@id']}
            openLinkInNewTab={true}
          >
            {content.title}
          </MaybeWrap>
        </h4>
        {content.description && (
          <div className={styles.representativeCardDescription}>
            {content.description}
          </div>
        )}
        {(content.jobtitle || content.state) && (
          <div className={styles.representativeCardDescription}>
            {jobtitle}
            {jobtitle && content.state && ', '}
            {content.state}
          </div>
        )}
        {!!address && (
          <div className={styles.representativeCardTextItem}>{address}</div>
        )}
        {content.email && (
          <div
            className={cx(
              styles.representativeCardLink,
              styles.representativeCardTextItem,
            )}
          >
            {userIsLogged ? (
              <a href={`mailto:${content.email}`}>{content.email}</a>
            ) : (
              <UniversalLink
                href={content.contact_link}
                openLinkInNewTab={true}
              >
                Contact me
              </UniversalLink>
            )}
          </div>
        )}
        {content.phone && (
          <div className={styles.representativeCardTextItem}>
            {intl.formatMessage(messages.phone_label)}: {content.phone}
          </div>
        )}
        {content.mobile && (
          <div className={styles.representativeCardTextItem}>
            {intl.formatMessage(messages.mobile_label)}: {content.mobile}
          </div>
        )}
      </div>
    </div>
  );
};

export default RepresentativesCards;

const messages = defineMessages({
  phone_label: {
    id: 'phone_label',
    defaultMessage: 'Phone',
  },
  mobile_label: {
    id: 'mobile_label',
    defaultMessage: 'Mobile',
  },
});
